import { animated, useSpring } from "@react-spring/web";
import type React from "react";
import { useState } from "react";
import { BuildConfigurationDisplay } from "./BuildConfigurationDisplay";
import { GlossyButton } from "./GlossyButton";
import { InstallationConfirmationModal } from "./InstallationConfirmationModal";

interface MappingResult {
  recommendedModules: Array<{
    name: string;
    technicalName: string;
    description: string;
    category: string;
    dependencies?: string[];
    securityRating: string;
    reasoning: string;
  }>;
  reasoning: string;
  dependencies: string[];
  resourceImpact?: {
    estimatedCpu: number;
    estimatedRam: number;
    estimatedStorage: number;
  };
}

interface ResultsPanelProps {
  result: MappingResult;
  onPrepareInstallation?: () => void;
  onExecuteInstallation?: () => void;
  loading: boolean;
  installationConfig?: any;
  showInstallationConfig?: boolean;
}

export const ResultsPanel: React.FC<ResultsPanelProps> = ({
  result,
  onPrepareInstallation,
  onExecuteInstallation,
  loading,
  installationConfig,
  showInstallationConfig = false,
}) => {
  const [showModuleList, setShowModuleList] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const slideIn = useSpring({
    from: { opacity: 0, transform: "translateX(-40px)" },
    to: { opacity: 1, transform: "translateX(0px)" },
    config: { tension: 280, friction: 60 },
  });

  const handlePrepareInstallation = () => {
    if (onPrepareInstallation) {
      onPrepareInstallation();
    }
  };

  const handleExecuteInstallation = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmExecution = () => {
    setShowConfirmModal(false);
    if (onExecuteInstallation) {
      onExecuteInstallation();
    }
  };

  return (
    <>
      <animated.div style={slideIn} className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
        {/* Show Installation Configuration if available */}
        {showInstallationConfig && installationConfig && (
          <div className="mb-8">
            <BuildConfigurationDisplay
              configuration={installationConfig}
              onExecute={handleExecuteInstallation}
              loading={loading}
            />
          </div>
        )}

        {/* Original Results Display */}
        {!showInstallationConfig && (
          <>
            {/* AI Reasoning */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Recommendation Analysis</h2>
              <p className="text-gray-700 leading-relaxed">{result.reasoning}</p>
            </div>

            {/* Recommended Modules Summary */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  Recommended Modules ({result.recommendedModules.length})
                </h3>
                <button
                  onClick={() => setShowModuleList(!showModuleList)}
                  className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                  type="button"
                >
                  {showModuleList ? "Hide Details" : "Show Details"}
                </button>
              </div>

              {/* Module Tags (Always Visible) */}
              <div className="flex flex-wrap gap-2 mb-4">
                {result.recommendedModules.map((module) => (
                  <span
                    key={module.technicalName}
                    className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm rounded-full font-medium"
                  >
                    {module.name}
                  </span>
                ))}
              </div>

              {/* Expanded Module Details */}
              {showModuleList && (
                <div className="space-y-4 border-t border-gray-200 pt-4">
                  {result.recommendedModules.map((module) => (
                    <div key={module.technicalName} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="text-lg font-semibold text-gray-900">{module.name}</h4>
                        <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded">
                          {module.category}
                        </span>
                      </div>
                      <p className="text-gray-700 text-sm mb-3">{module.description}</p>
                      <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                        <span>Technical: {module.technicalName}</span>
                        <span>Security: {module.securityRating}</span>
                      </div>
                      {module.dependencies && module.dependencies.length > 0 && (
                        <div className="mt-2 text-sm text-gray-600">
                          <span>Dependencies: {module.dependencies.join(", ")}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Dependencies */}
            {result.dependencies && result.dependencies.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Additional Dependencies</h3>
                <div className="flex flex-wrap gap-2">
                  {result.dependencies.map((dep) => (
                    <span key={dep} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                      {dep}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Resource Impact */}
            {result.resourceImpact && (
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Resource Requirements</h3>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      {result.resourceImpact.estimatedCpu}
                    </div>
                    <div className="text-sm text-gray-600">CPU Cores</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      {result.resourceImpact.estimatedRam}MB
                    </div>
                    <div className="text-sm text-gray-600">RAM</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      {result.resourceImpact.estimatedStorage}MB
                    </div>
                    <div className="text-sm text-gray-600">Storage</div>
                  </div>
                </div>
              </div>
            )}

            {/* Prepare Installation Button */}
            <div className="flex justify-center">
              <GlossyButton
                label={loading ? "Processing..." : "PREPARE INSTALLATION"}
                onClick={handlePrepareInstallation}
                loading={loading}
                disabled={loading}
                className="px-12 py-4 text-lg"
              />
            </div>
          </>
        )}
      </animated.div>

      {/* Installation Confirmation Modal */}
      {installationConfig && (
        <InstallationConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmExecution}
          configuration={{
            estimatedDuration: installationConfig.estimatedDuration || 300,
            estimatedCost: installationConfig.estimatedCost || 0,
            modules: installationConfig.modules || [],
          }}
        />
      )}
    </>
  );
};
